import { useState } from 'react';
import { CampaignRow, PageRow } from '../../types/marketing';
import { campaignLabel, formatCount, formatRange, formatRate, pageKindLabel, SortDir, sortByTaps } from './marketingMath';

type Tab = 'campaign' | 'page';

interface Props {
  byCampaign: CampaignRow[];
  byPage: PageRow[];
  from: string;
  to: string;
}

function SortHeader({ dir, onToggle }: { dir: SortDir; onToggle: () => void }) {
  return (
    <th scope="col" className="mkt-num" aria-sort={dir === 'desc' ? 'descending' : 'ascending'}>
      <button type="button" className="mkt-sort" onClick={onToggle} data-testid="sort-taps">
        Taps <span aria-hidden="true">{dir === 'desc' ? '↓' : '↑'}</span>
        <span className="sr-only">, sorted {dir === 'desc' ? 'high to low' : 'low to high'}. Activate to reverse.</span>
      </button>
    </th>
  );
}

/** Spec 078 AC-16 — taps, loads and rate by campaign (source / medium / campaign) or by page, sortable by taps. */
export default function CampaignBreakdownTable({ byCampaign, byPage, from, to }: Props) {
  const [tab, setTab] = useState<Tab>('campaign');
  const [dir, setDir] = useState<SortDir>('desc');
  const toggle = () => setDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  const rows = tab === 'campaign' ? byCampaign : byPage;

  return (
    <section className="mkt-card" aria-labelledby="mkt-breakdown-title">
      <div className="mkt-card__head">
        <h3 id="mkt-breakdown-title" className="mkt-card__title">Breakdown</h3>
        <div className="mkt-tabs" role="tablist" aria-label="Breakdown by">
          {([['campaign', 'By campaign'], ['page', 'By page']] as [Tab, string][]).map(([id, label]) => (
            <button key={id} type="button" role="tab" id={`mkt-tab-${id}`} aria-selected={tab === id} aria-controls="mkt-breakdown-panel"
              tabIndex={tab === id ? 0 : -1} className={`mkt-tab${tab === id ? ' mkt-tab--active' : ''}`} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div id="mkt-breakdown-panel" role="tabpanel" aria-labelledby={`mkt-tab-${tab}`}>
        {rows.length === 0 ? (
          <div className="mkt-empty" data-testid="breakdown-empty">
            <p className="mkt-empty__title">No store taps yet in this range</p>
            <p className="mkt-empty__range">{formatRange(from, to)}</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table mkt-table" data-testid="breakdown-table">
              <thead>
                <tr>
                  {tab === 'campaign'
                    ? <><th scope="col">Source</th><th scope="col">Medium</th><th scope="col">Campaign</th></>
                    : <><th scope="col">Page type</th><th scope="col">Item</th></>}
                  <SortHeader dir={dir} onToggle={toggle} />
                  <th scope="col" className="mkt-num">Page loads</th>
                  <th scope="col" className="mkt-num">Tap rate</th>
                </tr>
              </thead>
              <tbody>
                {tab === 'campaign'
                  ? sortByTaps(byCampaign, dir).map((r) => (
                    <tr key={campaignLabel(r)}>
                      <td>{r.source || '—'}</td><td>{r.medium || '—'}</td><td>{r.campaign || '—'}</td>
                      <td className="mkt-num">{formatCount(r.taps)}</td><td className="mkt-num">{formatCount(r.pageLoads)}</td><td className="mkt-num">{formatRate(r.tapRate)}</td>
                    </tr>
                  ))
                  : sortByTaps(byPage, dir).map((r) => (
                    <tr key={`${r.kind}:${r.itemId ?? ''}`}>
                      <td>{pageKindLabel(r.kind)}</td>
                      <td title={r.itemId ?? undefined}>{r.itemId ? <code className="mkt-id">{r.itemId.slice(0, 8)}</code> : '—'}</td>
                      <td className="mkt-num">{formatCount(r.taps)}</td><td className="mkt-num">{formatCount(r.pageLoads)}</td><td className="mkt-num">{formatRate(r.tapRate)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
